import React from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import {
  CategoryType,
  ContentMetricType
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import { useContentService } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useContentService';
import { KeyMetricsFilterAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/KeyMetricsAdapter';
import { ContentDropdownBarChart } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentBarChart';
import { ContentSplineChart } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentSplineChart';
import { ContentTableGrid } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentTableGrid';
import { ProductLevelContentUtilizationSummary } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ProductLevelContentUtilizationSummary';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useMainEntityType, useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { useContentChartSubtitles } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useDynamicSubtitle';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { formatMetric } from 'src/utils/app';
import { BarChartData } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/types';
import {
  createMetricColumnDefinition,
  createProductColumn
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/utils';
import { ContentUtilizationTopSection } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentUtilizationTopSection';

const ContentUtilizationPage = () => {
  const {
    getContentScoresKeyMetrics,
    getContentScoresByWeek,
    getContentScoresByProduct,
    getContentScoreByCategory,
    getProductLevelContentUtilizationDetails
  } = useContentService();

  const title = 'Content Utilization';
  const entityType = useMainEntityType();
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const { generateSplineChartSubtitle, generateBarChartSubtitle, generateTableGridSubtitle } =
    useContentChartSubtitles();

  const nbsp = String.fromCharCode(160);
  const columnDefinitions = [
    ['contentUtilizationScore', 'Utilization'],
    ['titleScore', 'Title'],
    ['featureBulletsScore', 'Bullets'],
    ['descriptionScore', 'Description'],
    ['imagesScore', 'Images'],
    ['videosScore', 'Videos'],
    ['aPlusScore', `Enhanced${nbsp}Content`]
  ].map(([id, header]) => createMetricColumnDefinition([id, header]));

  const groupByOptionsByEntityType = {
    default: [
      {
        id: 'category',
        label: 'Category',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentUtilizationScore, CategoryType.Category)
      },
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentUtilizationScore, CategoryType.Subcategory)
      },
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentUtilizationScore, CategoryType.Brand)
      }
    ],
    brand: [
      {
        id: 'category',
        label: 'Category',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentUtilizationScore, CategoryType.Category)
      },
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentUtilizationScore, CategoryType.Subcategory)
      }
    ],
    category: [
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentUtilizationScore, CategoryType.Subcategory)
      },
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentUtilizationScore, CategoryType.Brand)
      }
    ],
    subcategory: [
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentUtilizationScore, CategoryType.Brand)
      }
    ]
  };

  return (
    <BasePageContainer sx={{ display: 'flex', flexDirection: 'column', gap: `${BEACON_CHART_SPACING}px` }}>
      <ContentUtilizationTopSection
        type={ContentMetricType.ContentUtilizationScore}
        title="Key Metrics"
        queryFn={async () =>
          new KeyMetricsFilterAdapter(
            await getContentScoresKeyMetrics(
              ContentMetricType.ContentUtilizationScore,
              mainTimePeriod,
              comparisonTimePeriod
            ),
            {
              aPlusScore: 'Enhanced Content',
              descriptionScore: 'Description',
              featureBulletsScore: 'Bullets',
              imagesScore: 'Images',
              titleScore: 'Title',
              videosScore: 'Videos'
            }
          )
        }
      />

      {entityType === 'product' && (
        <ProductLevelContentUtilizationSummary
          queryFn={() => getProductLevelContentUtilizationDetails()}
          renderSubtitle={() => {
            return 'The section below displays the current utilization score at the component-level for this product.';
          }}
        />
      )}
      {entityType !== 'product' && (
        <>
          <ContentSplineChart
            title={title}
            queryFn={() =>
              getContentScoresByWeek(ContentMetricType.ContentUtilizationScore, mainTimePeriod, comparisonTimePeriod)
            }
            renderSubtitle={(score: string, change: number) => {
              return generateSplineChartSubtitle({
                formattedAverage: score,
                metricName: 'content utilization score',
                formattedPercentChange: formatMetric(Math.abs(change), METRICTYPE.PERCENT, { decimalPlaces: 2 }),
                direction: change > 0 ? 'higher' : 'lower'
              });
            }}
          />

          <ContentDropdownBarChart
            prefix={`${title} by`}
            options={groupByOptionsByEntityType[entityType] || groupByOptionsByEntityType.default}
            renderSubtitle={(selectedOption, data: BarChartData) => {
              const count = data.chartData[0].categories.length;

              const groupByText = (() => {
                switch (selectedOption) {
                  case 'category':
                    return count === 1 ? 'from 1 category' : `from all ${count} categories`;
                  case 'subcategory':
                    return count === 1 ? 'from 1 subcategory' : `from all ${count} subcategories`;
                  case 'brand':
                    return count === 1 ? 'from 1 brand' : `from all ${count} brands`;
                  default:
                    return '';
                }
              })();

              return generateBarChartSubtitle({
                metricName: 'content utilization score',
                groupByText
              });
            }}
          />

          <ContentTableGrid
            title={`${title} by Product`}
            productColumn={createProductColumn('341px')}
            columnDefinitions={columnDefinitions}
            queryFn={() =>
              getContentScoresByProduct(ContentMetricType.ContentUtilizationScore, mainTimePeriod, comparisonTimePeriod)
            }
            renderSubtitle={(view) => {
              return generateTableGridSubtitle({
                view,
                metricName: 'content utilization score'
              });
            }}
          />
        </>
      )}
    </BasePageContainer>
  );
};

export default ContentUtilizationPage;
